import React from 'react';
import { PrivacyPolicyContent } from '../../features/website/pages/PrivacyPolicyPage';
import { TermsConditionsContent } from '../../features/website/pages/TermsConditionsPage';
import { RefundPoliciesContent } from '../../features/website/pages/RefundPoliciesPage';
import { getConfig } from '../../config/appConfig';
import { defaultFrontendOrigin } from '../../config/environments';

// ***** Code Start BH29072026_1105 - Dashboard-base policy URLs (Sathis)
const getDashboardBase = () =>
  (
    getConfig('app.dashboardUrl') ||
    process.env.REACT_APP_DASHBOARD_URL ||
    defaultFrontendOrigin()
  ).replace(/\/+$/, '');

const POLICY_META = {
  privacy: {
    title: 'Candor – Privacy Policy',
    get externalUrl() { return `${getDashboardBase()}/privacy-policy`; },
  },
  terms: {
    title: 'AI End-User License Agreement & Terms of Service',
    get externalUrl() { return `${getDashboardBase()}/terms-and-policies`; },
  },
  refund: {
    title: 'Candor – Refund Policy',
    get externalUrl() { return `${getDashboardBase()}/refund-policy`; },
  },
};
// ***** Code End BH29072026_1105

/**
 * Full-screen policy dialog synced with website legal pages.
 */
export default function CompliancePolicyModal({ activePolicyId, onClose }) {
  if (!activePolicyId) return null;

  const meta = POLICY_META[activePolicyId];
  const title = meta?.title ?? 'Policy';
  const externalUrl = meta?.externalUrl ?? null;

  const renderContent = () => {
    switch (activePolicyId) {
      case 'privacy':
        return <PrivacyPolicyContent />;
      case 'terms':
        return <TermsConditionsContent />;
      case 'refund':
        return <RefundPoliciesContent />;
      default:
        return <p>Policy content is not available.</p>;
    }
  };

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] transition-opacity animate-fade-in"
      />

      <div className="fixed inset-0 flex items-center justify-center p-4 z-[201] pointer-events-none">
        <div
          role="dialog"
          aria-labelledby="compliance-policy-title"
          className="bg-white rounded-3xl shadow-2xl border border-slate-100 max-w-3xl w-full max-h-[85vh] flex flex-col pointer-events-auto animate-fade-in text-slate-800 font-sans"
        >
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center gap-4 shrink-0">
            <h3 id="compliance-policy-title" className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <span aria-hidden>📄</span>
              {title}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close policy"
              className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 overflow-y-auto text-sm text-slate-600 space-y-5 flex-1">
            {renderContent()}
          </div>

          <div className="px-6 py-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 shrink-0">
            {externalUrl ? (
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 underline"
              >
                View full policy on candorsys.com
              </a>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition border border-slate-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
